import { validateDomain } from '_utils/suffix';
import axios from 'axios';
import cn from 'classnames';
import { Link } from 'gatsby';
import React, { useMemo, useState } from 'react';
import { formSubmitTrack } from 'src/utils/analytics';
import { LABEL, PAID_SOCIAL } from 'src/utils/analytics/constants';
import { Hubspot } from '../../../constants/destinations';
import HighFive from '../../../images/Resources/high-five.svg';
import { ANALYTICS_FIELDS } from '../../../utils/analytics/hiddenFields';
import { getCountriesList } from '../../../utils/countries';
import { validateEmail } from '../../../utils/email';
import AdSpendInput from '../../AdSpendInput';
import Button, { ButtonSize, ButtonVariant } from '../../Button';
import CountryInput from '../../CountryInput';
import FormErrorMessage from '../../FormErrorMessage';
import Loading from '../../Loading';
import TermsAndConditions from '../../TermsAndConditions';

interface ResourceFormProps {
  buttonText?: string;
  successText?: string;
  resource?: any;
  setActiveModal?: React.Dispatch<React.SetStateAction<boolean>>;
  termsAndConditions?: string;
}

const ResourceForm: React.FC<ResourceFormProps> = props => {
  const {
    successText = 'Thanks for downloading!',
    buttonText = 'Download',
    resource,
    setActiveModal,
    termsAndConditions,
  } = props;

  const [email, setEmail] = React.useState<string>('');
  const [firstName, setFirstName] = React.useState<string>('');
  const [lastName, setLastName] = React.useState<string>('');
  const [company, setCompany] = React.useState<string>('');
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [inputError, setInputError] = React.useState<boolean>(false);
  const [adSpend, setAdSpend] = useState<{
    title?: string;
    hsID?: string;
  }>({ title: '', hsID: '' });
  const [countries, setCountries] = useState<
    {
      name: string;
      code: string;
    }[]
  >([]);
  const [location, setLocation] = useState<{
    name?: string;
    code?: string;
  }>({ name: '', code: '' });

  const typeOfForm =
    resource?.typeOfForm !== null ? resource?.typeOfForm[0] : 'Upper Funnel';

  useMemo(() => {
    const fetchCountries = async () => {
      const countriesList = await getCountriesList();
      setCountries(countriesList);
    };
    fetchCountries();
  }, []);

  const isValidForm = () => {
    if (typeOfForm === 'Lower Funnel' && resource.hubspotFormId != null) {
      return (
        firstName !== '' &&
        lastName !== '' &&
        validateEmail(email) &&
        validateDomain(email) &&
        company !== '' &&
        location.name !== '' &&
        adSpend.hsID !== ''
      );
    } else {
      return (
        firstName !== '' &&
        lastName !== '' &&
        validateEmail(email) &&
        validateDomain(email) &&
        company !== ''
      );
    }
  };
  const clearForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setCompany('');
    setLocation(null);
    setAdSpend(null);
  };

  const submitToHubSpot = () => {
    const portalId = Hubspot.portalId;
    const formGuid = typeOfForm === 'Lower Funnel' ? Hubspot.RESOURCES_DEFAULT_LOWER_FUNNEL_FORM_ID : Hubspot.RESOURCES_DEFAULT_UPPER_FUNNEL_FORM_ID
    
    let postData;
    if (typeOfForm === 'Lower Funnel' && resource.hubspotFormId != null) {
      postData = {
        portalId,
        formGuid,
        fields: [
          {
            name: 'firstname',
            value: firstName,
          },
          {
            name: 'lastname',
            value: lastName,
          },
          {
            name: 'email',
            value: email,
          },
          {
            name: 'company',
            value: company,
          },
          {
            name: 'country',
            value: location?.name,
          },
          {
            name: 'perpetua_ad_spend_tier',
            value: adSpend?.hsID,
          },
          {
            name: 'resource_title',
            value: resource?.title,
          },
          ...ANALYTICS_FIELDS(),
        ],
      };
    } else {
      postData = {
        portalId,
        formGuid,
        fields: [
          {
            name: 'firstname',
            value: firstName,
          },
          {
            name: 'lastname',
            value: lastName,
          },
          {
            name: 'email',
            value: email,
          },
          {
            name: 'company',
            value: company,
          },
          {
            name: 'resource_title',
            value: resource?.title,
          },
          ...ANALYTICS_FIELDS(),
        ],
      };
    }

    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };
    setInputError(false);

    axios
      .post(
        `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formGuid}`,
        postData,
        config
      )
      .then(res => {
        if (res.status == 200) {
          // no phone
          formSubmitTrack({
            label: LABEL.Downloadable,
            adSpend: adSpend?.hsID,
            paidSocialEvents: PAID_SOCIAL.downloadables,
            facebookPayload: {
              em: email,
              fn: firstName,
              ln: lastName,
              country: location?.name,
            },
          });

          clearForm();
          setLoading(false);
        }
      })
      .finally(() => {
        setTimeout(() => {
          setLoading(false);
          setSubmitted(true);
          window.open(resource?.resourceFileUrl, '_blank');
        }, 1000);
      })
      .catch(err => {
        if (err.response.status != 200) {
          console.log(err);
        }
      });
  };

  const buttonOnClick = () => {
    if (isValidForm()) {
      setLoading(true);
      submitToHubSpot();
    } else {
      setInputError(true);
    }
  };

  const sharedInputClasses =
    'newsletter-signup-input border border-gray-3 rounded-lg focus:border-indigo-1 focus:outline-none focus:ring-4 focus:ring-indigo-3-light py-2 px-4 w-full text-base mt-2';

  const sharedLabelClasses = 'text-base font-semibold';

  return (
    <div
      aria-hidden={true}
      className="max-w-full md:max-w-[600px] mx-auto mt-10"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label className={sharedLabelClasses}>First Name</label>
          <input
            className={cn(
              sharedInputClasses,
              inputError && firstName.length <= 0 && 'input-error'
            )}
            placeholder={'First Name'}
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
          />
        </div>
        <div>
          <label className={sharedLabelClasses}>Last Name</label>
          <input
            className={cn(
              sharedInputClasses,
              inputError && lastName.length <= 0 && 'input-error'
            )}
            placeholder={'Last Name'}
            value={lastName}
            onChange={e => setLastName(e.target.value)}
          />
        </div>
        <div>
          <label className={sharedLabelClasses}>Work Email</label>
          <input
            className={cn(
              sharedInputClasses,
              inputError &&
                (!validateEmail(email) || !validateDomain(email)) &&
                'input-error'
            )}
            placeholder={'Work Email'}
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
        </div>
        <div>
          <label className={sharedLabelClasses}>Company</label>
          <input
            className={cn(
              sharedInputClasses,
              inputError && company.length <= 0 && 'input-error'
            )}
            placeholder={'Company'}
            value={company}
            onChange={e => setCompany(e.target.value)}
          />
        </div>
        {typeOfForm === 'Lower Funnel' && resource.hubspotFormId != null ? (
          <>
            <div>
              <label className={`${sharedLabelClasses} mb-2 block`}>
                Where are you located?
              </label>
              <CountryInput
                countries={countries}
                location={location}
                setLocation={setLocation}
                inputError={inputError}
              />
            </div>
            <div>
              <label className={`${sharedLabelClasses} mb-2 block`}>
                What is your current monthly ad spend?
              </label>
              <AdSpendInput
                inputError={inputError}
                adSpend={adSpend}
                setAdSpend={setAdSpend}
              />
            </div>
          </>
        ) : (
          ''
        )}
      </div>

      {inputError && <FormErrorMessage />}

      <Button
        className="mx-auto my-10 py-4 px-8"
        variant={ButtonVariant.primary}
        text={buttonText}
        size={ButtonSize.large}
        onClick={e => {
          buttonOnClick();
        }}
      />

      <TermsAndConditions content={termsAndConditions} />

      {loading && (
        <div className="transition duration-200 absolute top-0 w-full h-full left-0 p-8 bg-white flex items-center justify-center rounded-xl">
          <Loading />
        </div>
      )}
      {submitted && (
        <div className="absolute w-full h-full left-0 top-0 p-8 bg-white flex items-center justify-center rounded-xl">
          <div className="mr-8 flex flex-col gap-8 items-center">
            <HighFive />
            <h3 className="text-4xl font-medium text-indigo-3-dark max-w-xs text-center leading-10">
              {successText}
            </h3>
            <Link className="text-indigo-1 underline" to="/resources/">
              Back to resources
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourceForm;
